import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { postService } from '../../services/api';
import Post from '../../components/Post/Post';
import CreatePost from '../../components/CreatePost/CreatePost';
import { FiImage, FiCalendar, FiBook, FiCamera, FiFolder } from 'react-icons/fi';
import './Home.css';

const Home = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);

  const fetchPosts = useCallback(async (pageNum = 1, append = false) => {
    try {
      if (pageNum === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const response = await postService.getNewsFeed(pageNum, 10);

      if (response.success) {
        if (append) {
          setPosts(prev => [...prev, ...response.data.posts]);
        } else {
          setPosts(response.data.posts);
        }
        setHasMore(response.data.hasMore);
      } else {
        setError('Failed to load posts');
      }
    } catch (err) {
      console.error('Failed to fetch posts:', err);
      setError('Failed to load posts. Please try again.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchPosts(nextPage, true);
    }
  };

  const handlePostCreated = (newPost) => {
    setPosts(prev => [newPost, ...prev]);
  };

  const handlePostUpdated = (updatedPost) => {
    setPosts(prev => prev.map(p =>
      p.postID === updatedPost.postID ? updatedPost : p
    ));
  };

  const handlePostDeleted = async (postId) => {
    try {
      const response = await postService.deletePost(postId);
      if (response.success) {
        setPosts(prev => prev.filter(p => p.postID !== postId));
      }
    } catch (err) {
      console.error('Failed to delete post:', err);
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  return (
    <div className="home-page">
      <div className="container">
        <div className="home-layout">
          {/* Main Feed */}
          <main className="feed-main">
            {/* Create Post Card */}
            <CreatePost user={user} onPostCreated={handlePostCreated} />

            {/* Posts */}
            {loading ? (
              <div className="feed-loading">
                <span className="spinner spinner-lg"></span>
                <p>Loading posts...</p>
              </div>
            ) : error ? (
              <div className="feed-error card">
                <p>{error}</p>
                <button className="btn btn-primary" onClick={() => fetchPosts()}>
                  Try Again
                </button>
              </div>
            ) : posts.length === 0 ? (
              <div className="feed-empty card">
                <div className="empty-state">
                  <div className="empty-state-icon">👋</div>
                  <h3 className="empty-state-title">Welcome to the Family!</h3>
                  <p>No posts yet. Be the first to share something with the family!</p>
                </div>
              </div>
            ) : (
              <>
                {posts.map(post => (
                  <Post
                    key={post.postID}
                    post={post}
                    onUpdate={handlePostUpdated}
                    onDelete={handlePostDeleted}
                  />
                ))}

                {hasMore && (
                  <div className="load-more">
                    <button
                      className="btn btn-secondary"
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                    >
                      {loadingMore ? (
                        <>
                          <span className="spinner spinner-sm"></span>
                          Loading...
                        </>
                      ) : (
                        'Load More'
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
          </main>

          {/* Sidebar - Desktop Only */}
          <aside className="feed-sidebar">
            {/* Profile Card */}
            <div className="sidebar-card card">
              <div className="profile-mini">
                <Link to="/profile" className="profile-mini-avatar">
                  {user?.profilePhotoURL ? (
                    <img src={user.profilePhotoURL} alt={user.firstName} className="avatar avatar-lg" />
                  ) : (
                    <div className="avatar avatar-lg">
                      {getInitials(user?.firstName, user?.lastName)}
                    </div>
                  )}
                </Link>
                <Link to="/profile" className="profile-mini-name">
                  {user?.firstName} {user?.lastName}
                </Link>
                <Link to="/profile" className="profile-mini-link">
                  View Profile
                </Link>
              </div>
            </div>

            {/* Quick Links */}
            <div className="sidebar-card card">
              <h4 className="sidebar-title">Quick Links</h4>
              <nav className="sidebar-nav">
                <Link to="/photos" className="sidebar-nav-item">
                  <FiImage />
                  <span>Photo Albums</span>
                </Link>
                <Link to="/events" className="sidebar-nav-item">
                  <FiCalendar />
                  <span>Family Events</span>
                </Link>
                <Link to="/event-albums" className="sidebar-nav-item">
                  <FiCamera />
                  <span>Event Albums</span>
                </Link>
                <Link to="/family-albums" className="sidebar-nav-item">
                  <FiFolder />
                  <span>Family Albums</span>
                </Link>
                <Link to="/stories" className="sidebar-nav-item">
                  <FiBook />
                  <span>Family Stories</span>
                </Link>
                <Link to="/recipes" className="sidebar-nav-item">
                  <FiBook />
                  <span>Family Recipes</span>
                </Link>
              </nav>
            </div>

            {/* Footer */}
            <div className="sidebar-footer">
              <p>The Pearson Clan &copy; {new Date().getFullYear()}</p>
              <p>Family Network</p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default Home;
